import defaultTheme from '../config/themes/default.json'
import christmasTheme from '../config/themes/christmas.json'
import valentinesTheme from '../config/themes/valentines.json'
import springTheme from '../config/themes/spring.json'
import easterTheme from '../config/themes/easter.json'
import summerTheme from '../config/themes/summer.json'
import autumnTheme from '../config/themes/autumn.json'
import halloweenTheme from '../config/themes/halloween.json'
import winterTheme from '../config/themes/winter.json'
import { getRuntimeConfig } from './runtimeConfig'

export interface ThemePalette {
  ivory: string
  ivoryDark: string
  gold: string
  goldLight: string
  goldGlow: string
  /** Brighter shade of the accent - used for the .btn-gold-wave shimmer streak. */
  goldHighlight: string
  /** Slightly less bright than goldHighlight - used for the shimmer transition stops. */
  goldHighlightSoft: string
  mocha: string
  mochaLight: string
}

export interface ThemeCampaign {
  id: string
  name: string
  /** MM-DD..MM-DD inclusive range (year-agnostic, recurring annually). Optional. */
  activeRange?: { start: string; end: string }
  palette: ThemePalette
}

// Note: the `--color-gold` slot in each palette is the campaign accent, not literally gold.
// A "summer" theme can paint this slot blue and every gold-named token (button border,
// underline, ribbon) follows.
//
// Order matters for "auto" resolution: `getActiveTheme` runs `Array.find` and
// returns the FIRST matching `activeRange`. Narrower holiday windows MUST come
// before broader seasonal windows that contain them - e.g. christmas (12-01..
// 12-31) sits inside winter (11-16..02-28), so christmas has to win for dates
// in December. Themes without `activeRange` (valentines, easter, halloween)
// are manual-only - runtime/env-pick - and never resolve via "auto".
export const ALL_THEMES: ReadonlyArray<ThemeCampaign> = [
  defaultTheme as ThemeCampaign,
  // Holidays first (narrow ranges)
  christmasTheme as ThemeCampaign,
  valentinesTheme as ThemeCampaign,
  easterTheme as ThemeCampaign,
  halloweenTheme as ThemeCampaign,
  // Then broad seasonal windows
  springTheme as ThemeCampaign,
  summerTheme as ThemeCampaign,
  autumnTheme as ThemeCampaign,
  winterTheme as ThemeCampaign,
]

const DEFAULT_ID = 'default'

function findById(id: string): ThemeCampaign | undefined {
  return ALL_THEMES.find((t) => t.id === id)
}

function parseMonthDay(s: string): { month: number; day: number } | null {
  const m = /^(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const month = Number(m[1])
  const day = Number(m[2])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return { month, day }
}

/**
 * True when `now` falls in the recurring MM-DD range.
 * Range is inclusive on both ends; ranges that wrap year-end (e.g. 12-15..01-05) are supported.
 */
export function isInRange(range: { start: string; end: string }, now: Date): boolean {
  const start = parseMonthDay(range.start)
  const end = parseMonthDay(range.end)
  if (!start || !end) return false
  const m = now.getMonth() + 1
  const d = now.getDate()
  const today = m * 100 + d
  const s = start.month * 100 + start.day
  const e = end.month * 100 + end.day
  return s <= e ? today >= s && today <= e : today >= s || today <= e
}

/**
 * Resolution order (Q26 - Hybrid):
 *   runtime.json `theme` → env VITE_THEME → 'auto' (date-range pick) → default.
 */
export function getActiveTheme(now: Date = new Date()): ThemeCampaign {
  const runtimeId = getRuntimeConfig().theme
  if (runtimeId && runtimeId !== 'auto') {
    const found = findById(runtimeId)
    if (found) return found
  }

  const envId = (import.meta.env.VITE_THEME as string | undefined)?.trim()
  if (envId && envId !== 'auto') {
    const found = findById(envId)
    if (found) return found
  }

  if (runtimeId === 'auto' || envId === 'auto') {
    const seasonal = ALL_THEMES.find((t) => t.activeRange && isInRange(t.activeRange, now))
    if (seasonal) return seasonal
  }

  // findById('default') always resolves because defaultTheme is in ALL_THEMES.
  return findById(DEFAULT_ID) as ThemeCampaign
}

/**
 * Pushes the active theme into CSS custom properties so Tailwind tokens
 * (defined as var(--color-…) elsewhere) reflect the campaign palette.
 */
export function applyThemeToDocument(theme: ThemeCampaign): void {
  const root = document.documentElement
  root.style.setProperty('--color-ivory', theme.palette.ivory)
  root.style.setProperty('--color-ivory-dark', theme.palette.ivoryDark)
  root.style.setProperty('--color-gold', theme.palette.gold)
  root.style.setProperty('--color-gold-light', theme.palette.goldLight)
  root.style.setProperty('--color-gold-glow', theme.palette.goldGlow)
  root.style.setProperty('--gold-highlight', theme.palette.goldHighlight)
  root.style.setProperty('--gold-highlight-soft', theme.palette.goldHighlightSoft)
  root.style.setProperty('--color-mocha', theme.palette.mocha)
  root.style.setProperty('--color-mocha-light', theme.palette.mochaLight)
  root.dataset.theme = theme.id
}
